import React, { useEffect } from 'react';
import { Form, Input, Select, Row, Col, Alert, Tooltip } from 'antd';
import { withDevModalFilePath, PhoneInput, EmailInput } from '../common';
import { phoneNumberRule } from '@/constants/validation';
import { useFormAutoBackup } from '../../hooks/useFormAutoBackup';

const { Modal } = withDevModalFilePath('client/src/components/user/UserFormModal.tsx');
const { Option } = Select;

export type UserRole = 'admin' | 'manager' | 'user' | 'viewer';

export interface UserFormValues extends Record<string, unknown> {
  name: string;
  email: string;
  password?: string;
  role: UserRole;
  department?: string;
  position?: string;
  phone?: string;
  avatarUrl?: string;
}

interface UserFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: UserFormValues) => Promise<void>;
  initialValues?: Partial<UserFormValues> | null;
  loading?: boolean;
  isEdit?: boolean;
  currentUserRole: UserRole;
  /** 編集対象が最後の管理者かどうか（trueの場合ロール変更不可） */
  isLastAdmin?: boolean;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  loading = false,
  isEdit = false,
  currentUserRole,
  isLastAdmin = false,
}) => {
  const [form] = Form.useForm<UserFormValues>();
  const isAdmin = currentUserRole === 'admin';

  // フォーム自動バックアップ（新規作成のみ有効、パスワードフィールドは除外）
  const { hasBackup, showRestoreConfirm, clearBackup } = useFormAutoBackup({    
    formType: 'user',
    entityId: undefined, // 新規作成のみなのでIDは不要
    form,
    enabled: visible && !isEdit,
    excludeFields: ['password'],
  });

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
      } else {
        form.resetFields();
      }
    }
  }, [visible, initialValues, form]);

  // モーダルが開かれた時にバックアップの確認を表示
  useEffect(() => {
    if (visible && hasBackup) {
      const timer = setTimeout(() => {
        showRestoreConfirm();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [visible, hasBackup, showRestoreConfirm]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values);
      clearBackup(); // 保存成功時にバックアップをクリア
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const getAvailableRoles = () => {
    if (isAdmin) {
      return [
        { value: 'admin', label: '管理者' },
        { value: 'manager', label: 'マネージャー' },
        { value: 'user', label: 'ユーザー' },
        { value: 'viewer', label: '閲覧者' },
      ];
    } else {
      // マネージャーは管理者を作成できない
      return [
        { value: 'manager', label: 'マネージャー' },
        { value: 'user', label: 'ユーザー' },
        { value: 'viewer', label: '閲覧者' },
      ];
    }
  };

  return (
    <Modal
      title={isEdit ? 'ユーザー情報編集' : '社内ユーザー作成'}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={600}
      okText={isEdit ? '更新' : '作成'}
      cancelText="キャンセル"
    >
      {!isEdit && (
        <div className="text-sm text-gray-600 mb-4">
          既存社員のアカウントを作成します。外部協力会社の担当者は「外部協力会社を招待」をご利用ください。
        </div>
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          role: 'user',
        }}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="name"
              label="氏名"
              rules={[
                { required: true, message: '氏名を入力してください' },
                { max: 100, message: '氏名は100文字以内で入力してください' },
              ]}
            >
              <Input placeholder="山田 太郎" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="email"
              label="メールアドレス"
              rules={[
                { required: true, message: 'メールアドレスを入力してください' },
                { type: 'email', message: '有効なメールアドレスを入力してください' },
              ]}
            >
              <EmailInput
                placeholder="yamada@example.com"
                disabled={isEdit} // 編集時はメールアドレス変更不可
              />
            </Form.Item>
          </Col>
        </Row>

        {!isEdit && (
          <Row gutter={16}>
            <Col span={24}>
              <Alert
                message="招待メールによるパスワード設定"
                description="ユーザー作成後、入力されたメールアドレス宛に初回パスワード設定用の招待メールが自動送信されます。ユーザーはメール内のリンクからパスワードを設定できます。"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Tooltip
              title={isLastAdmin && isEdit ? '管理者が1人のため、ロールを変更できません' : ''}
            >
              <Form.Item
                name="role"
                label="権限"
                rules={[{ required: true, message: '権限を選択してください' }]}
                extra={isLastAdmin && isEdit ? (
                  <span style={{ color: '#ff4d4f' }}>
                    管理者が1人のため、ロールを変更できません
                  </span>
                ) : undefined}
              >
                <Select
                  placeholder="権限を選択"
                  disabled={isLastAdmin && isEdit}
                >
                  {getAvailableRoles().map(role => (
                    <Option key={role.value} value={role.value}>
                      {role.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Tooltip>
          </Col>
          <Col span={12}>
            <Form.Item
              name="department"
              label="部署"
              rules={[
                { max: 100, message: '部署名は100文字以内で入力してください' },
              ]}
            >
              <Input placeholder="開発部" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="position"
              label="役職"
              rules={[
                { max: 100, message: '役職は100文字以内で入力してください' },
              ]}
            >
              <Input placeholder="主任" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phone"
              label="電話番号"
              rules={[
                { max: 20, message: '電話番号は20文字以内で入力してください' },
                phoneNumberRule(),
              ]}
            >
              <PhoneInput placeholder="090-1234-5678" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="avatarUrl"
              label="プロフィール画像URL"
              rules={[
                { type: 'url', message: '有効なURLを入力してください' },
              ]}
            >
              <Input placeholder="https://example.com/avatar.jpg" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default UserFormModal;
